export type EntityRefKind = "work_item" | "document";

export interface EntityReference {
  kind: EntityRefKind;
  /** Stable cache key: `work_item:FLOW-12`, `work_item:<uuid>` or `document:<uuid>` */
  key: string;
  /** Set when the reference was written as a display ID */
  displayId?: string;
  /** Set when the reference was written as an internal URL */
  id?: string;
}

const UUID_SOURCE =
  "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

/**
 * The app's entity routes (`/items/:id`, `/documents/:id`) and nothing else —
 * anchored, so `/x/items/<uuid>` and `/items/<uuid>/edit` are not references.
 */
const INTERNAL_PATH_RE = new RegExp(`^/(items|documents)/(${UUID_SOURCE})/?$`);

/** Candidate entity paths, used only to locate hrefs for validation. */
const ENTITY_PATH_SOURCE = `/(?:items|documents)/${UUID_SOURCE}`;

/** Characters that cannot appear in a URL token, so they bound one in prose. */
const URL_BOUNDARY_RE = /[\s<>"'`()[\]{}\\|]/;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * `PREFIX-NUMBER` for one specific project prefix, mirroring
 * `DISPLAY_ID_RE` in `backend/src/services/shipped_evidence.rs`: the reference
 * must not be preceded by an alphanumeric, a hyphen or a slash, so
 * `check-in-42`, `.../pull/FLOW-3` and `xFLOW-3` are not references.
 *
 * Returns `null` when no prefix is configured, which disables display-ID
 * matching entirely.
 */
export function createDisplayIdRegex(prefix: string): RegExp | null {
  if (!prefix) return null;
  return new RegExp(
    `(?:^|[^A-Za-z0-9/-])(${escapeRegExp(prefix)}-\\d{1,9})\\b`,
    "gi",
  );
}

/** The offset of the display ID itself, which is the tail of the whole match. */
export function displayIdOffset(match: RegExpExecArray): number {
  return match.index + match[0].length - match[1].length;
}

export function displayIdKey(displayId: string): string {
  return `work_item:${displayId.toUpperCase()}`;
}

function appOrigin(): string | null {
  if (typeof window === "undefined") return null;
  const origin = window.location?.origin;
  return origin && origin !== "null" ? origin : null;
}

/**
 * The pathname of `href` if it addresses this app, otherwise `null`.
 *
 * Resolution goes through `URL` rather than string matching so that a foreign
 * host cannot smuggle an entity path past the check — as an absolute URL, a
 * protocol-relative `//host/...`, or a query parameter. Outside a browser the
 * app's origin is unknown, so only root-relative paths can be called internal.
 */
function internalPathname(href: string): string | null {
  const trimmed = href.trim();
  if (!trimmed) return null;

  const origin = appOrigin();
  if (!origin) {
    return trimmed.startsWith("/") && !trimmed.startsWith("//")
      ? trimmed.split(/[?#]/)[0]
      : null;
  }

  try {
    const url = new URL(trimmed, origin);
    return url.origin === origin ? url.pathname : null;
  } catch {
    return null;
  }
}

/** The reference an href points at, or `null` if it is not an internal entity link. */
export function internalLinkRef(href: string): EntityReference | null {
  const pathname = internalPathname(href);
  if (!pathname) return null;

  const match = INTERNAL_PATH_RE.exec(pathname);
  if (!match) return null;

  const kind: EntityRefKind = match[1] === "items" ? "work_item" : "document";
  const id = match[2].toLowerCase();
  return { kind, key: `${kind}:${id}`, id };
}

/** The canonical in-app path for a resolved link reference. */
export function internalHref(reference: EntityReference): string {
  const segment = reference.kind === "work_item" ? "items" : "documents";
  return `/${segment}/${reference.id}`;
}

/**
 * Extend a candidate entity path backwards over the URL token containing it,
 * so an absolute or protocol-relative URL is validated as a whole rather than
 * by its tail. `[x](/items/<uuid>)` yields the path itself; `/x/items/<uuid>`
 * and `https://evil.example.com/items/<uuid>` yield tokens that fail the
 * origin and pathname checks.
 */
function urlTokenAt(
  text: string,
  matchStart: number,
  matchEnd: number,
): string {
  let start = matchStart;
  while (start > 0 && !URL_BOUNDARY_RE.test(text[start - 1])) {
    start -= 1;
  }
  return text.slice(start, matchEnd);
}

/** Extract every entity reference in `text`, first-seen order, deduplicated by `key`. */
export function parseReferences(
  text: string,
  prefix: string,
): EntityReference[] {
  if (!text) return [];

  const found: { index: number; reference: EntityReference }[] = [];

  const displayIdRegex = createDisplayIdRegex(prefix);
  if (displayIdRegex) {
    let match: RegExpExecArray | null;
    while ((match = displayIdRegex.exec(text)) !== null) {
      const displayId = match[1].toUpperCase();
      found.push({
        index: displayIdOffset(match),
        reference: {
          kind: "work_item",
          key: displayIdKey(displayId),
          displayId,
        },
      });
    }
  }

  const pathRegex = new RegExp(ENTITY_PATH_SOURCE, "g");
  let pathMatch: RegExpExecArray | null;
  while ((pathMatch = pathRegex.exec(text)) !== null) {
    const token = urlTokenAt(
      text,
      pathMatch.index,
      pathMatch.index + pathMatch[0].length,
    );
    const reference = internalLinkRef(token);
    if (reference) found.push({ index: pathMatch.index, reference });
  }

  found.sort((a, b) => a.index - b.index);

  const seen = new Set<string>();
  return found
    .filter(({ reference }) => {
      if (seen.has(reference.key)) return false;
      seen.add(reference.key);
      return true;
    })
    .map(({ reference }) => reference);
}
